import React, { useState } from "react";
import {
  FaFacebook,
  FaLinkedin,
  FaInstagram,
  FaArrowUp,
} from "react-icons/fa";
import {
  MdEmail,
  MdLocationOn,
  MdPhone,
  MdSecurity,
  MdSpeed,
  MdAnalytics 
} from "react-icons/md";
import { FiArrowRight, FiShield, FiUsers } from "react-icons/fi";
import logoImg from "../assets/spotless-logo-black.png";
import { Link } from "react-router-dom";
import { SiX } from "react-icons/si";
import { useTheme, useMediaQuery } from "@mui/material";

const accent = "#10b981";
const footerBg = "linear-gradient(180deg, #0f172a 0%, #0c1222 100%)";
const textPrimary = "#f8fafc";
const textMuted = "#94a3b8";
const textSoft = "#64748b";
const borderSubtle = "rgba(148, 163, 184, 0.12)";

const styles = {
  footer: {
    position: "relative",
    overflow: "hidden",
    marginTop: "auto",
    background: footerBg,
    borderTop: `1px solid ${borderSubtle}`,
  },
  accentBar: {
    height: "3px",
    width: "100%",
    background: `linear-gradient(90deg, transparent 0%, ${accent} 20%, ${accent} 80%, transparent 100%)`,
    opacity: 0.9,
  },
  container: {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "0 24px",
    position: "relative",
    zIndex: 2,
  },
  topSection: {
    display: "grid",
    gap: 48,
    padding: "56px 0 40px",
  },
  brandSection: {},
  logoLink: {
    display: "inline-flex",
    alignItems: "center",
    textDecoration: "none",
    borderRadius: 10,
    padding: "6px 8px",
    marginLeft: -8,
    marginBottom: 20,
    transition: "background 0.2s ease, transform 0.2s ease",
  },
  logo: {
    height: 44,
    width: "auto",
    borderRadius: 10,
    objectFit: "contain",
    padding: "8px",
    background: "white",
    border: "1px solid rgba(102, 126, 234, 0.2)",
    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.2)",
  },
  brandDescription: {
    color: textMuted,
    fontSize: "0.875rem",
    lineHeight: 1.6,
    margin: "0 0 20px 0",
    maxWidth: 280,
  },
  trustBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 14px",
    background: "rgba(16, 185, 129, 0.12)",
    border: `1px solid rgba(16, 185, 129, 0.25)`,
    borderRadius: 999,
    transition: "all 0.2s ease",
  },
  trustIcon: { color: accent },
  trustText: {
    color: textMuted,
    fontSize: "0.75rem",
    fontWeight: 600,
  },
  sectionTitle: {
    color: textPrimary,
    fontSize: "0.8125rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    margin: "0 0 20px 0",
  },
  linksList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
  },
  linkItem: { marginBottom: 10 },
  link: {
    color: textMuted,
    textDecoration: "none",
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: "0.875rem",
    fontWeight: 500,
    padding: "6px 0",
    transition: "color 0.2s ease, transform 0.2s ease",
    cursor: "pointer",
    background: "none",
    border: "none",
    font: "inherit",
  },
  linkIcon: { color: accent, flexShrink: 0 },
  contactList: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
    marginBottom: 24,
  },
  contactItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  contactIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: "rgba(16, 185, 129, 0.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: accent,
    flexShrink: 0,
    transition: "background 0.2s ease",
  },
  contactText: {
    color: textMuted,
    fontSize: "0.875rem",
    fontWeight: 500,
  },
  socialTitle: {
    color: "white",
    fontSize: "1rem",
    fontWeight: 600,
    margin: "0 0 16px 0",
  },
  socialLinks: {
    display: "flex",
    gap: 12,
  },
  socialLink: {
    width: 44,
    height: 44,
    borderRadius: 12,
    background: "rgba(255, 255, 255, 0.05)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#94a3b8",
    textDecoration: "none",
    transition: "all 0.3s ease",
  },
  bottomSection: {
    borderTop: `1px solid ${borderSubtle}`,
    padding: "24px 0",
  },
  bottomContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 20,
  },
  copyrightBlock: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  copyright: {
    color: textMuted,
    fontSize: "0.8125rem",
    margin: 0,
  },
  legalRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  legalLink: {
    color: textSoft,
    fontSize: "0.75rem",
    cursor: "pointer",
    background: "none",
    border: "none",
    padding: 0,
    font: "inherit",
    transition: "color 0.2s ease",
  },
  separator: { color: textSoft, fontSize: "0.75rem" },
  scrollTopBtn: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "linear-gradient(to right, #3B82F6 0%, #EC4899 100%)",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "12px 18px",
    fontSize: "0.8125rem",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  bgDecor: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: "none",
    zIndex: 1,
  },
  bgCircle1: {
    position: "absolute",
    top: -80,
    right: -80,
    width: 240,
    height: 240,
    background: `radial-gradient(circle, rgba(16, 185, 129, 0.06) 0%, transparent 70%)`,
    borderRadius: "50%",
  },
  bgCircle2: {
    position: "absolute",
    bottom: -120,
    left: -80,
    width: 280,
    height: 280,
    background: `radial-gradient(circle, rgba(16, 185, 129, 0.04) 0%, transparent 70%)`,
    borderRadius: "50%",
  },
};

const Footer = () => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.down("md"));
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));
  const [hoveredSocial, setHoveredSocial] = useState(null);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const quickLinks = [
    { label: "Dashboard", path: "/card" },
    { label: "User Management", path: "/users-list" },
    { label: "Global Settings", path: "/card-global" },
    { label: "Analytics", path: "/disinfection-analysis" },
  ];

  const adminTools = [
    { label: "System Monitoring", icon: <MdSpeed size={16} />, path: "/analytics/robots-list" },
    { label: "Security Center", icon: <MdSecurity size={16} />, path: "/security" },
    { label: "Performance Analytics", icon: <MdAnalytics size={16} />, path: "/disinfection-analysis" },
    { label: "User Reports", icon: <FiUsers size={16} />, path: "/analytics/robots-list" },
  ];

  const contactInfo = [
    { icon: <MdEmail size={18} />, text: "admin@spotless.ai", type: "email" },
    { icon: <MdPhone size={18} />, text: "+91 1235-45670", type: "phone" },
    {
      icon: <MdLocationOn size={18} />,
      text: "Admin Support Center, Salem",
      type: "location",
    },
  ];

  const socialLinks = [
    {
      href: "https://x.com/Spotless_ai",
      label: "X (formerly Twitter)",
      icon: <SiX size={18} />,
      key: "x",
      hoverColor: "#1da1f2",
    },
    {
      href: "https://www.linkedin.com/company/spotless-ai-ltd/",
      label: "LinkedIn",
      icon: <FaLinkedin size={18} />,
      key: "linkedin",
      hoverColor: "#0a66c2",
    },
    {
      href: "https://www.facebook.com/profile.php?id=100094543213378",
      label: "Facebook",
      icon: <FaFacebook size={18} />,
      key: "facebook",
      hoverColor: "#1877f2",
    },
    {
      href: "https://www.instagram.com/spotless_ai/",
      label: "Instagram",
      icon: <FaInstagram size={18} />,
      key: "instagram",
      hoverColor: "#e4405f",
    },
  ];

  const gridCols = isSm ? "1fr" : isMd ? "1fr 1fr" : "1.2fr 1fr 1fr 1fr";
  const brandColSpan = isSm ? "1" : isMd ? "1 / -1" : "1";
  const containerPadding = isSm ? "0 16px" : "0 24px";
  const topPadding = isSm ? "40px 0 32px" : "56px 0 40px";
  const brandAlign = isSm ? "center" : "left";
  const brandDescMaxWidth = isSm ? "100%" : 280;
  const bottomFlexDir = isSm ? "column" : "row";
  const bottomAlign = isSm ? "center" : "flex-start";
  const bottomTextAlign = isSm ? "center" : "left";

  return (
    <footer style={styles.footer}>
      <div style={styles.accentBar} />
      <div style={{ ...styles.container, padding: containerPadding }}>
        <div
          style={{
            ...styles.topSection,
            gridTemplateColumns: gridCols,
            padding: topPadding,
          }}
        >
          <div
            style={{
              ...styles.brandSection,
              gridColumn: brandColSpan,
              textAlign: brandAlign,
            }}
          >
            <Link
              to="/content"
              style={styles.logoLink}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                e.currentTarget.style.transform = "scale(1.02)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              <img src={logoImg} alt="Spotless AI Logo" style={styles.logo} />
            </Link>
            <p
              style={{
                ...styles.brandDescription,
                maxWidth: brandDescMaxWidth,
                marginLeft: isSm ? "auto" : 0,
                marginRight: isSm ? "auto" : 0,
              }}
            >
              Comprehensive administrative control for user management and system
              monitoring. Your command center for efficient operations.
            </p>
            <div
              style={{
                ...styles.trustBadge,
                marginLeft: isSm ? "auto" : 0,
                marginRight: isSm ? "auto" : 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(16, 185, 129, 0.18)";
                e.currentTarget.style.borderColor = "rgba(16, 185, 129, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(16, 185, 129, 0.12)";
                e.currentTarget.style.borderColor = "rgba(16, 185, 129, 0.25)";
              }}
            >
              <FiShield size={16} style={styles.trustIcon} />
              <span style={styles.trustText}>Trusted by 200+ Organizations</span>
            </div>
          </div>

          <div style={{ textAlign: isSm ? "center" : "left" }}>
            <h4 style={styles.sectionTitle}>Quick Access</h4>
            <ul style={styles.linksList}>
              {quickLinks.map((link, index) => (
                <li key={index} style={styles.linkItem}>
                  <Link
                    to={link.path}
                    style={{ ...styles.link, color: textMuted }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = accent;
                      e.currentTarget.style.transform = "translateX(4px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = textMuted;
                      e.currentTarget.style.transform = "translateX(0)";
                    }}
                  >
                    <FiArrowRight size={14} style={styles.linkIcon} />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div style={{ textAlign: isSm ? "center" : "left" }}>
            <h4 style={styles.sectionTitle}>Admin Tools</h4>
            <ul style={styles.linksList}>
              {adminTools.map((tool, index) => (
                <li key={index} style={styles.linkItem}>
                  <Link
                    to={tool.path}
                    style={{ ...styles.link, color: textMuted }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = accent;
                      e.currentTarget.style.transform = "translateX(4px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = textMuted;
                      e.currentTarget.style.transform = "translateX(0)";
                    }}
                  >
                    <span style={styles.linkIcon}>{tool.icon}</span>
                    {tool.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div style={{ textAlign: isSm ? "center" : "left" }}>
            <h4 style={styles.sectionTitle}>Admin Support</h4>
            <div style={styles.contactList}>
              {contactInfo.map((contact, index) => (
                <div key={index} style={styles.contactItem}>
                  <div
                    style={styles.contactIcon}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background =
                        "rgba(16, 185, 129, 0.2)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background =
                        "rgba(16, 185, 129, 0.12)";
                    }}
                  >
                    {contact.icon}
                  </div>
                  <span style={styles.contactText}>{contact.text}</span>
                </div>
              ))}
            </div>
            <h5 style={styles.socialTitle}>Connect With Us</h5>
            <div
              style={{
                ...styles.socialLinks,
                justifyContent: isSm ? "center" : "flex-start",
              }}
            >
              {socialLinks.map(({ href, label, icon, key, hoverColor }) => (
                <a
                  key={key}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  style={{
                    ...styles.socialLink,
                    color: hoveredSocial === key ? hoverColor : "#94a3b8",
                    background:
                      hoveredSocial === key
                        ? "rgba(102, 126, 234, 0.2)"
                        : "rgba(255, 255, 255, 0.05)",
                    borderColor:
                      hoveredSocial === key
                        ? "rgba(102, 126, 234, 0.3)"
                        : "rgba(255, 255, 255, 0.1)",
                    boxShadow:
                      hoveredSocial === key
                        ? "0 8px 25px rgba(102, 126, 234, 0.3)"
                        : "none",
                    transform: hoveredSocial === key ? "translateY(-2px)" : "none",
                  }}
                  onMouseEnter={() => setHoveredSocial(key)}
                  onMouseLeave={() => setHoveredSocial(null)}
                >
                  {icon}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div style={styles.bottomSection}>
          <div
            style={{
              ...styles.bottomContent,
              flexDirection: bottomFlexDir,
              alignItems: bottomAlign,
              textAlign: bottomTextAlign,
            }}
          >
            <div style={styles.copyrightBlock}>
              <p style={styles.copyright}>
                © 2026 Spotless AI. All rights reserved. | Admin Portal v2.1
              </p>
              <div
                style={{
                  ...styles.legalRow,
                  justifyContent: isSm ? "center" : "flex-start",
                }}
              >
                <button
                  type="button"
                  style={styles.legalLink}
                  onClick={() => console.log("Privacy Policy clicked")}
                  onMouseEnter={(e) => (e.currentTarget.style.color = accent)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = textSoft)}
                >
                  Privacy Policy
                </button>
                <span style={styles.separator}>•</span>
                <button
                  type="button"
                  style={styles.legalLink}
                  onClick={() => console.log("Terms of Service clicked")}
                  onMouseEnter={(e) => (e.currentTarget.style.color = accent)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = textSoft)}
                >
                  Terms of Service
                </button>
                <span style={styles.separator}>•</span>
                <button
                  type="button"
                  style={styles.legalLink}
                  onClick={() => console.log("Security clicked")}
                  onMouseEnter={(e) => (e.currentTarget.style.color = accent)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = textSoft)}
                >
                  Security
                </button>
              </div>
            </div>
            <button
              onClick={scrollToTop}
              style={styles.scrollTopBtn}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "linear-gradient(to right, #2563eb 0%, #db2777 100%)";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(59, 130, 246, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "linear-gradient(to right, #3B82F6 0%, #EC4899 100%)";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <FaArrowUp size={16} />
              <span>Back to top</span>
            </button>
          </div>
        </div>
      </div>

      <div style={styles.bgDecor}>
        <div style={styles.bgCircle1} />
        <div style={styles.bgCircle2} />
      </div>
    </footer>
  );
};

export default Footer;
